import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Layout from '../components/Layout';
import LoadingTimeout from '../components/LoadingTimeout';
import { useLoadingTimeout } from '../hooks/useLoadingTimeout';
import { useRegisterRefresh } from '../context/RefreshContext';
import { useAuth } from '../context/AuthContext';
import { canUseFeature, getRequiredPlan } from '../utils/planLimits';
import { UpgradePrompt } from '../components/UpgradePrompt';
import {
  relatorios as relatoriosApi,
  meses as mesesApi,
  receitas as receitasApi,
  descontos as descontosApi,
  despesasRecorrentes as despesasRecApi,
  despesasEsporadicas as despesasEspApi,
} from '../lib/api';
import { formatCurrency, getMonthName, getMonthNameShort } from '../utils/format';
import {
  BarChart3,
  TrendingUp,
  Calculator,
  Calendar,
  DollarSign,
  PiggyBank,
  Award,
  AlertTriangle,
  Download,
  FileText,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

// ---- PDF color palette (RGB) ----
const PDF_NAVY = [27, 42, 74];       // #1B2A4A
const PDF_GREEN = [4, 120, 87];      // #047857
const PDF_AMBER = [217, 119, 6];     // #D97706
const PDF_RED = [185, 28, 28];       // #B91C1C
const PDF_ROW_ALT = [249, 250, 251]; // #F9FAFB
const PDF_BOX_GREEN = [239, 249, 244]; // #EFF9F4
const PDF_SLATE = [100, 116, 139];   // #64748B

const PDF_MARGIN = 20;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;

const isMobileDevice = () =>
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || '');

function formatDateBR(d = new Date()) {
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function money(value) {
  const n = Number(value) || 0;
  return `R$ ${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function addPdfFooter(doc) {
  const pageCount = doc.getNumberOfPages();
  const genDate = formatDateBR();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...PDF_SLATE);
    doc.text(
      'Bolso Sob Controle — bolsosobcontrole.com.br',
      PDF_MARGIN,
      PAGE_HEIGHT - 10
    );
    doc.text(
      `Página ${i} de ${pageCount}`,
      PAGE_WIDTH / 2,
      PAGE_HEIGHT - 10,
      { align: 'center' }
    );
    doc.text(
      `Gerado em ${genDate}`,
      PAGE_WIDTH - PDF_MARGIN,
      PAGE_HEIGHT - 10,
      { align: 'right' }
    );
  }
}

function drawHeader(doc, subtitle, accentLine) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(...PDF_NAVY);
  doc.text('Bolso Sob Controle', PDF_MARGIN, 28);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(13);
  doc.setTextColor(...PDF_SLATE);
  doc.text(subtitle, PDF_MARGIN, 36);

  if (accentLine) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...PDF_GREEN);
    doc.text(accentLine, PDF_MARGIN, 44);
  }

  // Green separator line
  doc.setDrawColor(...PDF_GREEN);
  doc.setLineWidth(0.6);
  doc.line(PDF_MARGIN, 48, PAGE_WIDTH - PDF_MARGIN, 48);
}

// Renders a labeled summary box with a list of {label, value, color} lines.
// Returns the Y position after the box.
function drawSummaryBox(doc, startY, lines) {
  const boxX = PDF_MARGIN;
  const boxW = PAGE_WIDTH - PDF_MARGIN * 2;
  const lineHeight = 7;
  const paddingY = 6;
  const boxH = paddingY * 2 + lines.length * lineHeight;

  doc.setFillColor(...PDF_BOX_GREEN);
  doc.roundedRect(boxX, startY, boxW, boxH, 2, 2, 'F');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  let y = startY + paddingY + 5;
  lines.forEach(({ label, value, color, bold }) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...PDF_NAVY);
    doc.text(`${label}:`, boxX + 4, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...(color || PDF_NAVY));
    doc.text(value, boxX + boxW - 4, y, { align: 'right' });
    y += lineHeight;
  });

  return startY + boxH;
}

function renderTable(doc, startY, title, head, body) {
  if (title) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...PDF_NAVY);
    doc.text(title, PDF_MARGIN, startY);
    startY += 4;
  }

  autoTable(doc, {
    startY: startY + 2,
    head: [head],
    body,
    theme: 'striped',
    styles: { font: 'helvetica', fontSize: 10, cellPadding: 3 },
    headStyles: {
      fillColor: PDF_NAVY,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'left',
    },
    alternateRowStyles: { fillColor: PDF_ROW_ALT },
    margin: { left: PDF_MARGIN, right: PDF_MARGIN },
  });

  return doc.lastAutoTable.finalY;
}

async function deliverPdf(doc, filename) {
  const blob = doc.output('blob');

  if (isMobileDevice() && typeof navigator.share === 'function') {
    try {
      const file = new File([blob], filename, { type: 'application/pdf' });
      if (!navigator.canShare || navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: filename,
          text: 'Relatório financeiro — Bolso Sob Controle',
        });
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return; // user cancelled
      // fall through to download
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function fetchMonthDetail(mes, ano) {
  const mesRow = await mesesApi.getOrCreate(mes, ano);
  const mesId = mesRow?.id || mesRow;
  const [receitas, descontos, despRec, despEsp] = await Promise.all([
    receitasApi.list(mesId),
    descontosApi.list(mesId),
    despesasRecApi.list(mesId),
    despesasEspApi.list(mesId),
  ]);
  return { mesId, receitas, descontos, despRec, despEsp };
}

async function generateSingleMonthPdf(mes, ano, mesSummary) {
  const { receitas, descontos, despRec, despEsp } = await fetchMonthDetail(mes, ano);

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const monthName = getMonthName(mes);

  drawHeader(doc, 'Relatório Financeiro Mensal', `${monthName} de ${ano}`);

  const totalBruto = mesSummary?.total_bruto ?? receitas.reduce((s, r) => s + Number(r.valor_bruto || 0), 0);
  const totalDescontos = mesSummary?.total_descontos ?? descontos.reduce((s, d) => s + Number(d.valor || 0), 0);
  const totalLiquido = mesSummary?.total_liquido ?? (totalBruto - totalDescontos);
  const totalDespesas = mesSummary?.total_despesas ?? (
    despRec.reduce((s, d) => s + Number(d.valor || 0), 0) +
    despEsp.reduce((s, d) => s + Number(d.valor || 0), 0)
  );
  const saldo = mesSummary?.saldo_mensal ?? (totalLiquido - totalDespesas);

  const summaryEndY = drawSummaryBox(doc, 54, [
    { label: 'Receita Líquida', value: money(totalLiquido), color: PDF_GREEN, bold: true },
    { label: 'Total de Despesas', value: money(totalDespesas), color: PDF_AMBER, bold: true },
    { label: 'Saldo do Mês', value: money(saldo), color: saldo >= 0 ? PDF_GREEN : PDF_RED, bold: true },
    { label: 'Receita Bruta', value: money(totalBruto) },
    { label: 'Total de Descontos', value: money(totalDescontos) },
  ]);

  // ---- Income + deductions table ----
  let cursorY = summaryEndY + 10;

  const descontosByDescricao = new Map();
  descontos.forEach((d) => {
    const key = (d.descricao || d.tipo || '').toLowerCase().trim();
    descontosByDescricao.set(key, (descontosByDescricao.get(key) || 0) + Number(d.valor || 0));
  });

  const receitasBody = receitas.map((r) => {
    const bruto = Number(r.valor_bruto || 0);
    const descTotal = 0; // per-receita link not modeled; show zeros per row
    return [
      r.descricao || '—',
      r.tipo === 'esporadica' ? 'Esporádica' : 'Fixa',
      money(bruto),
      money(descTotal),
      money(bruto - descTotal),
    ];
  });

  if (descontos.length > 0) {
    descontos.forEach((d) => {
      receitasBody.push([
        d.descricao || d.tipo || '—',
        'Desconto',
        money(0),
        money(Number(d.valor || 0)),
        money(-Number(d.valor || 0)),
      ]);
    });
  }

  if (receitasBody.length === 0) {
    receitasBody.push(['Nenhuma receita registrada', '', '', '', '']);
  }

  cursorY = renderTable(
    doc,
    cursorY,
    'Receitas e Descontos',
    ['Descrição', 'Tipo', 'Valor Bruto', 'Descontos', 'Líquido'],
    receitasBody
  );

  // ---- Expenses table ----
  cursorY += 12;
  if (cursorY > PAGE_HEIGHT - 60) {
    doc.addPage();
    cursorY = PDF_MARGIN + 5;
  }

  const allDespesas = [
    ...despRec.map((d) => ({ ...d, _tipo: 'Fixa' })),
    ...despEsp.map((d) => ({ ...d, _tipo: 'Esporádica' })),
  ];

  const despesasBody = allDespesas.length === 0
    ? [['Nenhuma despesa registrada', '', '', '', '']]
    : allDespesas.map((d) => [
        d.descricao || '—',
        d.categoria || '—',
        money(Number(d.valor || 0)),
        d._tipo,
        d.pago ? 'Pago' : 'Pendente',
      ]);

  renderTable(
    doc,
    cursorY,
    'Despesas do Mês',
    ['Descrição', 'Categoria', 'Valor', 'Tipo', 'Status'],
    despesasBody
  );

  addPdfFooter(doc);

  return { doc, filename: `BolsoSobControle_${monthName}${ano}.pdf` };
}

async function generateYearPdf(ano, relatorio) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  drawHeader(doc, 'Relatório Financeiro Anual', `Ano de ${ano}`);

  const totais = relatorio?.totais || {};
  const melhor = relatorio?.melhor_mes;
  const pior = relatorio?.pior_mes;
  const media = relatorio?.media_saldo_mensal || 0;
  const saldoAno = totais.saldo_mensal || 0;

  const summaryLines = [
    { label: 'Receita Líquida do Ano', value: money(totais.total_liquido || 0), color: PDF_GREEN, bold: true },
    { label: 'Despesas do Ano', value: money(totais.total_despesas || 0), color: PDF_AMBER, bold: true },
    { label: 'Saldo Total do Ano', value: money(saldoAno), color: saldoAno >= 0 ? PDF_GREEN : PDF_RED, bold: true },
    { label: 'Média Mensal de Saldo', value: money(media) },
  ];
  if (melhor) {
    summaryLines.push({
      label: `Melhor Mês (${getMonthName(melhor.mes)}/${melhor.ano})`,
      value: money(melhor.saldo_mensal),
      color: PDF_GREEN,
    });
  }
  if (pior) {
    summaryLines.push({
      label: `Pior Mês (${getMonthName(pior.mes)}/${pior.ano})`,
      value: money(pior.saldo_mensal),
      color: PDF_RED,
    });
  }

  let cursorY = drawSummaryBox(doc, 54, summaryLines);

  // Monthly summary table (only months with data)
  const mesesComDados = (relatorio?.meses || []).filter(
    (m) => (m.total_bruto || 0) > 0 || (m.total_despesas || 0) > 0
  );

  cursorY += 10;

  if (mesesComDados.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(...PDF_SLATE);
    doc.text('Nenhum mês com dados neste ano.', PDF_MARGIN, cursorY + 10);
    addPdfFooter(doc);
    return { doc, filename: `BolsoSobControle_Anual${ano}.pdf` };
  }

  const monthlyBody = mesesComDados.map((m) => [
    getMonthName(m.mes),
    money(m.total_liquido || 0),
    money(m.total_despesas || 0),
    {
      content: money(m.saldo_mensal || 0),
      styles: {
        textColor: (m.saldo_mensal || 0) >= 0 ? PDF_GREEN : PDF_RED,
        fontStyle: 'bold',
      },
    },
  ]);

  monthlyBody.push([
    { content: 'TOTAL', styles: { fontStyle: 'bold', textColor: [255, 255, 255], fillColor: PDF_NAVY } },
    { content: money(totais.total_liquido || 0), styles: { fontStyle: 'bold', textColor: [255, 255, 255], fillColor: PDF_NAVY } },
    { content: money(totais.total_despesas || 0), styles: { fontStyle: 'bold', textColor: [255, 255, 255], fillColor: PDF_NAVY } },
    { content: money(saldoAno), styles: { fontStyle: 'bold', textColor: [255, 255, 255], fillColor: PDF_NAVY } },
  ]);

  cursorY = renderTable(
    doc,
    cursorY,
    'Resumo Mensal',
    ['Mês', 'Receita Líquida', 'Despesas', 'Saldo'],
    monthlyBody
  );

  // Per-month detail sections
  for (const m of mesesComDados) {
    doc.addPage();
    let y = PDF_MARGIN + 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...PDF_GREEN);
    doc.text(`${getMonthName(m.mes)} de ${ano}`, PDF_MARGIN, y);
    y += 8;

    y = drawSummaryBox(doc, y, [
      { label: 'Receita Líquida', value: money(m.total_liquido || 0), color: PDF_GREEN, bold: true },
      { label: 'Despesas', value: money(m.total_despesas || 0), color: PDF_AMBER, bold: true },
      { label: 'Saldo', value: money(m.saldo_mensal || 0), color: (m.saldo_mensal || 0) >= 0 ? PDF_GREEN : PDF_RED, bold: true },
    ]);

    y += 10;

    try {
      const { despRec, despEsp } = await fetchMonthDetail(m.mes, ano);
      const allDespesas = [
        ...despRec.map((d) => ({ ...d, _tipo: 'Fixa' })),
        ...despEsp.map((d) => ({ ...d, _tipo: 'Esporádica' })),
      ];

      const body = allDespesas.length === 0
        ? [['Nenhuma despesa registrada', '', '', '', '']]
        : allDespesas.map((d) => [
            d.descricao || '—',
            d.categoria || '—',
            money(Number(d.valor || 0)),
            d._tipo,
            d.pago ? 'Pago' : 'Pendente',
          ]);

      renderTable(
        doc,
        y,
        'Despesas do Mês',
        ['Descrição', 'Categoria', 'Valor', 'Tipo', 'Status'],
        body
      );
    } catch (err) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(...PDF_SLATE);
      doc.text('Não foi possível carregar o detalhamento deste mês.', PDF_MARGIN, y + 4);
    }
  }

  addPdfFooter(doc);

  return { doc, filename: `BolsoSobControle_Anual${ano}.pdf` };
}

function RelatoriosPage() {
  const { user } = useAuth();
  const plan = user?.plan || 'gratuito';
  const [activeTab, setActiveTab] = useState('anual');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [relatorio, setRelatorio] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Investment calculator state
  const [calcForm, setCalcForm] = useState({
    valor_inicial: '',
    aporte_mensal: '',
    taxa_anual: '10',
    periodo_anos: '5',
  });
  const [calcResult, setCalcResult] = useState(null);

  // PDF export state
  const [exportMonth, setExportMonth] = useState('');
  const [exportingMonth, setExportingMonth] = useState(false);
  const [exportingYear, setExportingYear] = useState(false);

  // Load annual report
  const loadRelatorio = useCallback(async () => {
    setLoading(true);
    try {
      const response = await relatoriosApi.getRelatorioAnual(selectedYear);
      setRelatorio(response);
    } catch (error) {
      console.error('Failed to load relatorio:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadRelatorio();
  }, [loadRelatorio]);

  const { timedOut, resetTimeout } = useLoadingTimeout(loading, loadRelatorio);
  useRegisterRefresh(loadRelatorio);

  // Months with data for the single-month export selector.
  const monthsWithData = useMemo(() => {
    if (!relatorio?.meses) return [];
    return relatorio.meses.filter(
      (m) => (m.total_bruto || 0) > 0 || (m.total_despesas || 0) > 0
    );
  }, [relatorio]);

  // Default export month = most recent month with data for the selected year.
  useEffect(() => {
    if (monthsWithData.length === 0) {
      setExportMonth('');
      return;
    }
    const latest = [...monthsWithData].sort((a, b) => b.mes - a.mes)[0];
    setExportMonth(String(latest.mes));
  }, [monthsWithData]);

  const handleExportMonth = async () => {
    const mesNum = parseInt(exportMonth, 10);
    if (!mesNum) return;
    const mesSummary = monthsWithData.find((m) => m.mes === mesNum);
    setExportingMonth(true);
    try {
      const { doc, filename } = await generateSingleMonthPdf(
        mesNum,
        selectedYear,
        mesSummary
      );
      await deliverPdf(doc, filename);
    } catch (err) {
      console.error('Failed to generate single-month PDF:', err);
      toast.error('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setExportingMonth(false);
    }
  };

  const handleExportYear = async () => {
    if (!relatorio) return;
    setExportingYear(true);
    try {
      const { doc, filename } = await generateYearPdf(selectedYear, relatorio);
      await deliverPdf(doc, filename);
    } catch (err) {
      console.error('Failed to generate annual PDF:', err);
      toast.error('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setExportingYear(false);
    }
  };

  // Calculate investment
  const calculateInvestment = () => {
    const valorInicial = parseFloat(calcForm.valor_inicial.replace(',', '.')) || 0;
    const aporteMensal = parseFloat(calcForm.aporte_mensal.replace(',', '.')) || 0;
    const taxaAnual = parseFloat(calcForm.taxa_anual.replace(',', '.')) || 0;
    const periodoAnos = parseInt(calcForm.periodo_anos) || 1;
    
    const taxaMensal = taxaAnual / 100 / 12;
    const meses = periodoAnos * 12;
    
    // Calculate compound interest with monthly contributions
    let montante = valorInicial;
    const history = [{ mes: 0, montante: valorInicial, investido: valorInicial }];
    let totalInvestido = valorInicial;
    
    for (let i = 1; i <= meses; i++) {
      montante = montante * (1 + taxaMensal) + aporteMensal;
      totalInvestido += aporteMensal;
      
      if (i % 12 === 0 || i === meses) {
        history.push({
          mes: i,
          ano: Math.ceil(i / 12),
          montante: Math.round(montante * 100) / 100,
          investido: Math.round(totalInvestido * 100) / 100,
        });
      }
    }
    
    const jurosGanhos = montante - totalInvestido;
    
    setCalcResult({
      montanteFinal: montante,
      totalInvestido,
      jurosGanhos,
      history,
    });
  };

  // Prepare chart data
  const chartData = relatorio?.meses?.map((m) => ({
    name: getMonthNameShort(m.mes),
    'Receita Líquida': m.total_liquido,
    'Despesas': m.total_despesas,
    'Saldo': m.saldo_mensal,
  })) || [];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  if (loading) {
    return (
      <Layout showMonthSelector={false}>
        {timedOut ? (
          <LoadingTimeout
            timedOut={timedOut}
            onRetry={() => {
              resetTimeout();
              loadRelatorio();
            }}
          />
        ) : (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#1B2A4A] border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg font-semibold text-slate-600">Carregando relatórios...</p>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout showMonthSelector={false}>
      <div className="space-y-6 animate-fade-in" data-testid="relatorios-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BarChart3 size={28} className="text-[#1B2A4A]" />
          <h2 className="text-2xl font-bold text-[#1B2A4A]">Relatórios</h2>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-14 bg-slate-100 rounded-xl p-1">
            <TabsTrigger 
              value="anual" 
              className="h-12 text-base font-semibold rounded-lg data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"
              data-testid="tab-anual"
            >
              <Calendar size={18} className="mr-2" />
              Relatório Anual
            </TabsTrigger>
            <TabsTrigger 
              value="calculadora"
              className="h-12 text-base font-semibold rounded-lg data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white"
              data-testid="tab-calculadora"
            >
              <Calculator size={18} className="mr-2" />
              Calculadora
            </TabsTrigger>
          </TabsList>

          {/* Annual Report Tab */}
          <TabsContent value="anual" className="mt-4 space-y-6">
            {!canUseFeature(plan, 'annualReport') ? (
              <UpgradePrompt feature="acessar o relatório anual" requiredPlan={getRequiredPlan('annualReport')} />
            ) : (<>
            {/* Year Selector */}
            <div className="flex items-center gap-4">
              <Label className="text-lg font-semibold">Ano:</Label>
              <Select
                value={selectedYear.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="w-32 h-12 text-lg border-2" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()} className="text-lg">
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Export PDF */}
            {!canUseFeature(plan, 'pdfExport') ? (
              <UpgradePrompt feature="exportar relatórios em PDF" requiredPlan={getRequiredPlan('pdfExport')} />
            ) : (
            <Card className="border-2 border-amber-200 bg-amber-50 shadow-sm" data-testid="export-pdf-card">
              <CardContent className="pt-4 space-y-4">
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-amber-700" />
                  <h3 className="text-base font-bold text-amber-900">
                    Baixar relatório em PDF
                  </h3>
                </div>

                {/* Single month */}
                <div className="space-y-2">
                  <Label className="text-base font-semibold text-[#1B2A4A]">
                    Selecionar mês para exportar
                  </Label>
                  <Select
                    value={exportMonth}
                    onValueChange={setExportMonth}
                    disabled={monthsWithData.length === 0}
                  >
                    <SelectTrigger
                      className="h-12 text-base border-2 bg-white"
                      data-testid="export-month-select"
                    >
                      <SelectValue
                        placeholder={
                          monthsWithData.length === 0
                            ? 'Nenhum mês com dados'
                            : 'Escolha um mês'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {monthsWithData
                        .slice()
                        .sort((a, b) => a.mes - b.mes)
                        .map((m) => (
                          <SelectItem
                            key={m.mes}
                            value={String(m.mes)}
                            className="text-base"
                          >
                            {getMonthName(m.mes)} de {m.ano}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action buttons — stacked on mobile, side-by-side on md+ */}
                <div className="flex flex-col md:flex-row gap-3">
                  <Button
                    onClick={handleExportMonth}
                    disabled={
                      !exportMonth || exportingMonth || exportingYear || monthsWithData.length === 0
                    }
                    className="h-12 w-full md:flex-1 text-base font-semibold bg-[#1B2A4A] hover:bg-[#2C3E50] text-white rounded-xl"
                    data-testid="export-month-btn"
                  >
                    {exportingMonth ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Gerando PDF...
                      </>
                    ) : (
                      <>
                        <Download size={20} className="mr-2" />
                        Baixar mês específico
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleExportYear}
                    disabled={exportingMonth || exportingYear || monthsWithData.length === 0}
                    className="h-12 w-full md:flex-1 text-base font-semibold bg-green-600 hover:bg-green-700 text-white rounded-xl"
                    data-testid="export-year-btn"
                  >
                    {exportingYear ? (
                      <>
                        <Loader2 size={20} className="mr-2 animate-spin" />
                        Gerando PDF...
                      </>
                    ) : (
                      <>
                        <Download size={20} className="mr-2" />
                        Baixar ano completo
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Summary Cards */}
            {relatorio?.totais && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-2 border-slate-200">
                  <CardContent className="pt-4">
                    <p className="text-sm font-semibold text-slate-500">Receita Líquida Anual</p>
                    <p className="text-xl font-bold text-[#1B2A4A] tabular-nums">
                      {formatCurrency(relatorio.totais.total_liquido)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-slate-200">
                  <CardContent className="pt-4">
                    <p className="text-sm font-semibold text-slate-500">Despesas Anuais</p>
                    <p className="text-xl font-bold text-amber-600 tabular-nums">
                      {formatCurrency(relatorio.totais.total_despesas)}
                    </p>
                  </CardContent>
                </Card>
                <Card className={`border-2 ${relatorio.totais.saldo_mensal >= 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                  <CardContent className="pt-4">
                    <p className={`text-sm font-semibold ${relatorio.totais.saldo_mensal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      Saldo Anual
                    </p>
                    <p className={`text-xl font-bold tabular-nums ${relatorio.totais.saldo_mensal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(relatorio.totais.saldo_mensal)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-slate-200">
                  <CardContent className="pt-4">
                    <p className="text-sm font-semibold text-slate-500">Média Mensal</p>
                    <p className="text-xl font-bold text-[#1B2A4A] tabular-nums">
                      {formatCurrency(relatorio.media_saldo_mensal)}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Best/Worst Month */}
            {(relatorio?.melhor_mes || relatorio?.pior_mes) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatorio.melhor_mes && (
                  <Card className="border-2 border-green-300 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-green-700 flex items-center gap-2">
                        <Award size={20} />
                        Melhor Mês
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-bold text-green-700">
                        {getMonthNameShort(relatorio.melhor_mes.mes)}/{relatorio.melhor_mes.ano}
                      </p>
                      <p className="text-lg text-green-600">
                        Saldo: {formatCurrency(relatorio.melhor_mes.saldo_mensal)}
                      </p>
                    </CardContent>
                  </Card>
                )}
                {relatorio.pior_mes && (
                  <Card className="border-2 border-red-300 bg-red-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg font-bold text-red-700 flex items-center gap-2">
                        <AlertTriangle size={20} />
                        Pior Mês
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xl font-bold text-red-700">
                        {getMonthNameShort(relatorio.pior_mes.mes)}/{relatorio.pior_mes.ano}
                      </p>
                      <p className="text-lg text-red-600">
                        Saldo: {formatCurrency(relatorio.pior_mes.saldo_mensal)}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Chart */}
            {chartData.length > 0 && (
              <Card className="border-2 border-slate-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-[#1B2A4A]">
                    Evolução Mensal - {selectedYear}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                        <YAxis 
                          tick={{ fontSize: 12 }} 
                          tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ fontSize: '14px', borderRadius: '8px' }}
                        />
                        <Legend />
                        <Bar dataKey="Receita Líquida" fill="#047857" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Despesas" fill="#D97706" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Saldo" fill="#1B2A4A" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Monthly Table */}
            <Card className="border-2 border-slate-200 shadow-lg overflow-hidden">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#1B2A4A]">
                  Detalhamento por Mês
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-base">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-bold text-[#1B2A4A]">Mês</th>
                        <th className="px-4 py-3 text-right font-bold text-[#1B2A4A]">Receita Líq.</th>
                        <th className="px-4 py-3 text-right font-bold text-[#1B2A4A]">Despesas</th>
                        <th className="px-4 py-3 text-right font-bold text-[#1B2A4A]">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relatorio?.meses?.map((m, i) => (
                        <tr key={m.mes} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-4 py-3 font-semibold">{getMonthNameShort(m.mes)}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(m.total_liquido)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-amber-600">{formatCurrency(m.total_despesas)}</td>
                          <td className={`px-4 py-3 text-right tabular-nums font-bold ${m.saldo_mensal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(m.saldo_mensal)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-[#1B2A4A] text-white font-bold">
                        <td className="px-4 py-3">TOTAL</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(relatorio?.totais?.total_liquido || 0)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(relatorio?.totais?.total_despesas || 0)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(relatorio?.totais?.saldo_mensal || 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>)}
          </TabsContent>

          {/* Calculator Tab */}
          <TabsContent value="calculadora" className="mt-4 space-y-6">
            {!canUseFeature(plan, 'projection12Month') ? (
              <UpgradePrompt feature="usar a calculadora de investimentos" requiredPlan={getRequiredPlan('projection12Month')} />
            ) : (
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-[#1B2A4A] flex items-center gap-3">
                  <Calculator size={24} />
                  Calculadora de Investimentos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-base text-slate-600">
                  Simule quanto seu dinheiro pode render com juros compostos. 
                  Ideal para planejar sua aposentadoria ou economias.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Valor Inicial (R$)</Label>
                    <Input
                      value={calcForm.valor_inicial}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, valor_inicial: e.target.value }))}
                      placeholder="1.000,00"
                      className="h-14 text-lg border-2"
                      data-testid="calc-valor-inicial"
                    />
                    <p className="text-sm text-slate-500">Quanto você tem para investir agora</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Aporte Mensal (R$)</Label>
                    <Input
                      value={calcForm.aporte_mensal}
                      onChange={(e) => setCalcForm(prev => ({ ...prev, aporte_mensal: e.target.value }))}
                      placeholder="500,00"
                      className="h-14 text-lg border-2"
                      data-testid="calc-aporte"
                    />
                    <p className="text-sm text-slate-500">Quanto você vai depositar todo mês</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Taxa de Juros Anual (%)</Label>
                    <Select
                      value={calcForm.taxa_anual}
                      onValueChange={(value) => setCalcForm(prev => ({ ...prev, taxa_anual: value }))}
                    >
                      <SelectTrigger className="h-14 text-lg border-2" data-testid="calc-taxa">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6" className="text-lg">6% (Poupança)</SelectItem>
                        <SelectItem value="10" className="text-lg">10% (CDI/CDB)</SelectItem>
                        <SelectItem value="12" className="text-lg">12% (Tesouro Direto)</SelectItem>
                        <SelectItem value="15" className="text-lg">15% (Fundos)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-500">Rendimento esperado por ano</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-base font-semibold">Período (Anos)</Label>
                    <Select
                      value={calcForm.periodo_anos}
                      onValueChange={(value) => setCalcForm(prev => ({ ...prev, periodo_anos: value }))}
                    >
                      <SelectTrigger className="h-14 text-lg border-2" data-testid="calc-periodo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 5, 10, 15, 20, 25, 30].map((year) => (
                          <SelectItem key={year} value={year.toString()} className="text-lg">
                            {year} {year === 1 ? 'ano' : 'anos'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-500">Por quanto tempo vai investir</p>
                  </div>
                </div>

                <Button
                  onClick={calculateInvestment}
                  className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                  data-testid="calc-btn"
                >
                  <Calculator size={20} className="mr-2" />
                  Calcular
                </Button>

                {/* Results */}
                {calcResult && (
                  <div className="space-y-6 pt-6 border-t-2 border-slate-200">
                    <h3 className="text-xl font-bold text-[#1B2A4A]">Resultado da Simulação</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Card className="border-2 border-[#1B2A4A] bg-slate-50">
                        <CardContent className="pt-4 text-center">
                          <DollarSign size={32} className="mx-auto text-[#1B2A4A] mb-2" />
                          <p className="text-sm font-semibold text-slate-500">Total Investido</p>
                          <p className="text-2xl font-bold text-[#1B2A4A] tabular-nums">
                            {formatCurrency(calcResult.totalInvestido)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="border-2 border-green-400 bg-green-50">
                        <CardContent className="pt-4 text-center">
                          <TrendingUp size={32} className="mx-auto text-green-600 mb-2" />
                          <p className="text-sm font-semibold text-green-700">Juros Ganhos</p>
                          <p className="text-2xl font-bold text-green-700 tabular-nums">
                            {formatCurrency(calcResult.jurosGanhos)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="border-2 border-[#1B2A4A] bg-[#1B2A4A]">
                        <CardContent className="pt-4 text-center">
                          <PiggyBank size={32} className="mx-auto text-white mb-2" />
                          <p className="text-sm font-semibold text-slate-300">Montante Final</p>
                          <p className="text-2xl font-bold text-white tabular-nums">
                            {formatCurrency(calcResult.montanteFinal)}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Growth Chart */}
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={calcResult.history}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis 
                            dataKey="ano" 
                            tick={{ fontSize: 14 }}
                            tickFormatter={(value) => value ? `Ano ${value}` : 'Início'}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => `R$ ${(value/1000).toFixed(0)}k`}
                          />
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ fontSize: '14px', borderRadius: '8px' }}
                          />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="investido" 
                            name="Total Investido"
                            stackId="1"
                            stroke="#1B2A4A" 
                            fill="#1B2A4A" 
                            fillOpacity={0.3}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="montante" 
                            name="Montante (com juros)"
                            stackId="2"
                            stroke="#047857" 
                            fill="#047857"
                            fillOpacity={0.6}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Tips */}
                    <Card className="border-2 border-amber-300 bg-amber-50">
                      <CardContent className="pt-4">
                        <h4 className="text-lg font-bold text-amber-800 mb-2">Dica: Juros Compostos</h4>
                        <p className="text-base text-amber-700">
                          Com juros compostos, você ganha juros sobre juros. Quanto mais tempo investir, 
                          maior será o efeito "bola de neve" no seu dinheiro. 
                          Mesmo pequenas quantias mensais podem se transformar em grandes valores ao longo dos anos!
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

export default RelatoriosPage;
